import { ReactNode, useEffect, useState } from 'react'
import { Alert, Anchor, Badge, Button, Center, Group, Loader, Pagination, Stack, Tabs, Text, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconArrowBackUp, IconClipboardText, IconInbox } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryDeskCounts, useQueryDeskQueue } from 'hooks/desk'
import { useDeskDecision } from 'hooks/desk/useDeskDecision'
import { useAppTranslation } from 'hooks/useTranslation'
import { DeskQueuePage, DeskQueueRow } from 'supabase/desk'
import { DeskTable, deskRowKey } from './DeskTable'
import { DiscardedRawItemModal } from './DiscardedRawItemModal'
import { deskImagePaths, publicImageUrl } from './deskImages'
import { PasteDrawer, usePasteSession } from './PasteDrawer'
import { RejectMenu, RejectNoteHost, deskTabName, showDeskError } from './RejectMenu'
import { DESK_PAGE_SIZE, DESK_TAB_LABEL_KEYS, DESK_TABS, DeskTab, readDeskTab, readPage } from './deskTabs'

const EMPTY_KEYS: Record<DeskTab, { title: string; body: string }> = {
  ready: { title: 'desk.empty.ready_title', body: 'desk.empty.ready_body' },
  needs_a_look: { title: 'desk.empty.needs_a_look_title', body: 'desk.empty.needs_a_look_body' },
  discard: { title: 'desk.empty.discard_title', body: 'desk.empty.discard_body' },
  updates: { title: 'desk.empty.updates_title', body: 'desk.empty.updates_body' }
}

/** Router state a candidate screen reads to send "Back to {tab}" to the same tab and page. */
export type DeskReturnState = { tab: DeskTab; page: number }

/**
 * The Desk (DESK-01, D-01..D-04, UI-SPEC §B): Dominique's daily queue of
 * editor candidates in four tabs. Tab and page are URL state, read through a
 * closed vocabulary (deskTabs.ts), so a shared or bookmarked link reopens the
 * same view and nothing off the URL reaches a query unchecked.
 *
 * The tab badges come from desk_counts() only (D-03), never from a fetched
 * row count, and refetch every 60 s and after every decision.
 *
 * Admin-only: the route sits inside <AdminRoute>, and desk_queue is a
 * security_invoker view over admin-only RLS (the real wall, 33-12).
 */
export const Desk = () => {
  const { t } = useAppTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = readDeskTab(searchParams.get('tab'))
  const page = readPage(searchParams.get('page'))

  const queryClient = useQueryClient()
  const { isLoading, isFetching, isError, rows, count, outOfRange } = useQueryDeskQueue({
    tab,
    page,
    perPage: DESK_PAGE_SIZE
  })
  const counts = useQueryDeskCounts()

  // The same key useQueryDeskQueue reads. A decision takes its row off this
  // page at once and puts it back if the write fails; the decision hook's
  // invalidation then re-reads the page and the badges either way.
  const queueKey = ['desk', 'queue', { tab, page, perPage: DESK_PAGE_SIZE }]

  const removeRow = (row: DeskQueueRow) => {
    const previous = queryClient.getQueryData<DeskQueuePage>(queueKey)
    if (!previous) return () => undefined
    const key = deskRowKey(row)
    queryClient.setQueryData<DeskQueuePage>(queueKey, {
      rows: previous.rows.filter((candidate) => deskRowKey(candidate) !== key),
      count: previous.count === null ? null : Math.max(0, previous.count - 1)
    })
    return () => queryClient.setQueryData<DeskQueuePage>(queueKey, previous)
  }

  const { restore, restoreRawItem } = useDeskDecision()
  const [restoringKey, setRestoringKey] = useState<string | null>(null)
  const [rawItemRow, setRawItemRow] = useState<DeskQueueRow | null>(null)

  // The paste session lives here, not in the drawer (U-07): closing the
  // drawer never cancels a paste, and reopening shows where it got to.
  const paste = usePasteSession({ restoreRawItem: restoreRawItem.mutateAsync })
  const [pasteOpen, setPasteOpen] = useState(false)

  const pasteButton = (inHeader: boolean) => (
    <Button
      variant="default"
      leftSection={<IconClipboardText size={16} />}
      rightSection={inHeader && paste.inFlight ? <Loader size="xs" data-testid="paste-in-flight" /> : undefined}
      onClick={() => setPasteOpen(true)}
    >
      {t('paste.open')}
    </Button>
  )

  const showRestored = (id: number, tabName: string) => {
    const toastId = `desk-restored-${id}`
    notifications.show({
      id: toastId,
      color: 'green',
      message: (
        <Group gap={8}>
          <Text size="sm">{t('desk.restore.done', { tab: tabName })}</Text>
          {/* The notifications host sits outside the router: navigate by hand. */}
          <Anchor
            size="sm"
            href={`/desk/${id}`}
            onClick={(event) => {
              event.preventDefault()
              notifications.hide(toastId)
              navigate(`/desk/${id}`)
            }}
          >
            {t('desk.restore.open')}
          </Anchor>
        </Group>
      )
    })
  }

  /**
   * Restore (DESK-06, D-12), one click and no confirm. A candidate goes where
   * desk_restore says (its route's tab; Needs a look after an auto-reject); a
   * raw item becomes a new candidate in Needs a look.
   */
  const restoreRow = async (row: DeskQueueRow): Promise<boolean> => {
    const key = deskRowKey(row)
    if (restoringKey !== null) return false
    setRestoringKey(key)
    const rollback = removeRow(row)
    try {
      if (row.application_id !== null) {
        const restoredTo = await restore.mutateAsync(row.application_id)
        showRestored(row.application_id, deskTabName(t, restoredTo))
      } else {
        if (row.raw_item_id === null) throw new Error('desk row has no raw item')
        const [firstImage] = deskImagePaths(row)
        const newId = await restoreRawItem.mutateAsync({
          rawItemId: row.raw_item_id,
          mainPicture: firstImage ? publicImageUrl(firstImage) : null
        })
        showRestored(newId, t('desk.tabs.needs_a_look'))
      }
      return true
    } catch (error) {
      rollback()
      showDeskError(t, error)
      return false
    } finally {
      setRestoringKey(null)
    }
  }

  const restoreFromModal = async (row: DeskQueueRow) => {
    if (await restoreRow(row)) setRawItemRow(null)
  }

  const renderActions = (row: DeskQueueRow): ReactNode => {
    if (tab === 'discard') {
      const key = deskRowKey(row)
      return (
        <Button
          size="xs"
          variant="light"
          leftSection={<IconArrowBackUp size={16} />}
          loading={restoringKey === key}
          disabled={restoringKey !== null}
          onClick={(event) => {
            event.stopPropagation()
            restoreRow(row)
          }}
        >
          {t('desk.restore.button')}
        </Button>
      )
    }
    if (tab !== 'ready' && tab !== 'needs_a_look') return null
    if (row.application_id === null) return null
    return (
      <RejectMenu
        id={row.application_id}
        title={row.event_title}
        variant="icon"
        onRejecting={() => removeRow(row)}
      />
    )
  }

  // Mutate-and-re-set, as on Applications: a fresh object would drop every
  // other param. A tab change resets the page, since page 4 of another tab
  // may not exist.
  const changeTab = (value: string | null) => {
    searchParams.set('tab', readDeskTab(value))
    searchParams.set('page', '1')
    setSearchParams(searchParams)
  }

  const changePage = (next: number) => {
    searchParams.set('tab', tab)
    searchParams.set('page', next.toString())
    setSearchParams(searchParams)
  }

  // Review AD-WR-05: deciding the last row of the last page re-reads a page
  // past the end. Move to the new last page (page 1 when the tab emptied),
  // or one page back when the range error carried no count, instead of
  // showing the admin load error. `replace`: the gone page is not history.
  const lastPage = count === null ? null : Math.max(1, Math.ceil(count / DESK_PAGE_SIZE))
  const clampTo =
    isLoading || isFetching || isError
      ? null
      : outOfRange
        ? lastPage ?? Math.max(1, page - 1)
        : lastPage !== null && page > lastPage
          ? lastPage
          : null
  const clamping = clampTo !== null && clampTo !== page
  useEffect(() => {
    if (!clamping || clampTo === null) return
    searchParams.set('tab', tab)
    searchParams.set('page', clampTo.toString())
    setSearchParams(searchParams, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clamping, clampTo, tab])

  const openCandidate = (row: DeskQueueRow) => {
    if (row.application_id === null) return
    const state: DeskReturnState = { tab, page }
    navigate(`/desk/${row.application_id}`, { state })
  }

  const countBadge = (value: DeskTab): ReactNode => {
    const active = value === tab
    const count = counts.counts?.[value]
    const badge = (
      <Badge
        size="sm"
        fz={12}
        variant="light"
        color={active ? 'blue' : 'gray'}
        circle={false}
        aria-label={
          counts.isError
            ? t('desk.tabs.count_unavailable')
            : count === undefined
              ? undefined
              : t('desk.tabs.count_aria', { count })
        }
      >
        {count === undefined || counts.isError ? '–' : count}
      </Badge>
    )
    if (!counts.isError) return badge
    return <Tooltip label={t('desk.tabs.count_unavailable')}>{badge}</Tooltip>
  }

  const totalPages = count ? Math.ceil(count / DESK_PAGE_SIZE) : 0
  const tabRows = rows ?? []

  const body = (): ReactNode => {
    if (isError) {
      return <Alert color="red">{t('desk.error.load')}</Alert>
    }
    if (isLoading || clamping) {
      return <Text c="dimmed">{t('common.loading')}</Text>
    }
    if (tabRows.length === 0) {
      return (
        <Stack align="center" gap={8} py={48}>
          <IconInbox size={32} color="var(--mantine-color-gray-5)" />
          <Text size="md" fw={600} lh={1.3}>
            {t(EMPTY_KEYS[tab].title)}
          </Text>
          <Text size="sm" c="dimmed" ta="center" maw={480}>
            {t(EMPTY_KEYS[tab].body)}
          </Text>
          {(tab === 'ready' || tab === 'needs_a_look') && pasteButton(false)}
        </Stack>
      )
    }
    return (
      <DeskTable
        tab={tab}
        rows={tabRows}
        onOpenCandidate={openCandidate}
        onOpenRawItem={setRawItemRow}
        renderActions={renderActions}
      />
    )
  }

  return (
    <Stack gap={16} style={{ padding: 24 }}>
      {/* mb 16 on top of the Stack's 16 gap: 32 between header and tabs (UI-SPEC §Spacing). */}
      <Group justify="space-between" mb={16}>
        <Text size="xl" fw={600} lh={1.3}>
          {t('desk.title')}
        </Text>
        {/* Never filled: in the drawer "Send to the editor" is the only filled button (U-15). */}
        {pasteButton(true)}
      </Group>

      <Tabs value={tab} onChange={changeTab}>
        <Tabs.List>
          {DESK_TABS.map((value) => (
            <Tabs.Tab key={value} value={value} rightSection={countBadge(value)}>
              {t(DESK_TAB_LABEL_KEYS[value])}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      {body()}

      {totalPages > 1 && (
        <Center mt={24}>
          <Pagination value={page} total={totalPages} onChange={changePage} />
        </Center>
      )}

      <RejectNoteHost />

      <PasteDrawer opened={pasteOpen} onClose={() => setPasteOpen(false)} session={paste} />

      <DiscardedRawItemModal
        row={rawItemRow}
        onClose={() => setRawItemRow(null)}
        onRestore={restoreFromModal}
        restoring={restoringKey !== null}
      />
    </Stack>
  )
}
